import { memo, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { agentsActions, analyticsActions, channelsActions } from '@actions';
import { CHANNEL } from '@constants/channels';
import { GroupByFilter } from '@constants/analytics';
import { subtractYears } from '@helpers/dates';
import { useChannels } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import FiltersPanel from './FiltersPanel';
import ConversationsRates from './ConversationsRates';
import ConversationsTable from './ConversationsTable';
import ConversationsTrend from './ConversationsTrend';

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const Conversations = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        channels: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const { data: channels, loading: channelsLoading } = useSelector(state => state.channels);
    const { conversationsData, conversationsLoading } = useSelector(state => state.analytics);
    const language = useSelector(state => state.auth.user?.language);
    const { data: agents } = useSelector(state => state.agentsLookup);

    const dispatch = useDispatch();
    const { getAccessibleChannelIds } = useChannels();

    useEffect(() => {
        dispatch(channelsActions.get());
        dispatch(agentsActions.getLookup());
    }, []);

    useEffect(() => {
        if (!channelsLoading) {
            setFilter(prev => ({
                ...prev,
                channels: [...getAccessibleChannelIds(c => c?.incoming), CHANNEL.ARCHIVED],
            }));
        }
    }, [channelsLoading, channels, dispatch]);

    useEffect(() => {
        if (filter.channels.length) {
            if (firstRender.current) {
                onApplyFilters();
                firstRender.current = false;
            }
        }
    }, [filter, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsConversations({
                channels: filter.channels,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
        );
    };

    return (
        <StyledAnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="conversations"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-conversations">
                <ConversationsRates data={conversationsData.rates} isLoading={conversationsLoading} />
                <ConversationsTrend data={conversationsData.trendData} language={language} grouping={filter.grouping} />
                <ConversationsTable
                    data={conversationsData.tableData}
                    isLoading={conversationsLoading}
                    agents={agents}
                    filter={filter}
                />
            </AnalyticsContainer>
        </StyledAnalyticsWrapper>
    );
});

Conversations.displayName = 'Conversations';

export default Conversations;
